import React, { useState, useRef, useEffect } from 'react';
import { IoSearchOutline, IoChevronDownOutline, IoAddOutline } from 'react-icons/io5';

const MultiSelectDropdown = ({ 
    label, 
    options = [], 
    selected = [], 
    onSelect, 
    onClearAll,
    placeholder = "Search...",
    className = "",
    alignRight = false,
    disabled = false,
    showFooter = true,
    addOptionLabel = null,
    onAddOption = null,
    isSingle = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const filteredOptions = options.filter(opt => 
        (opt.name || opt).toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = (optionName) => {
        onSelect(optionName);
        if (isSingle) {
            setIsOpen(false);
        }
    };

    return (
        <div className={`multi-select-dropdown position-relative ${className}`} ref={dropdownRef}>
            <button 
                className="regionbtn btn d-flex align-items-center justify-content-between gap-1 py-1 px-2 border"
                style={{ 
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer'
                }}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) setIsOpen(!isOpen);
                }}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={`Select ${label}`}
            >
                <div className="d-flex align-items-center gap-1 overflow-hidden">
                    <span className="text-dark fw-bold text-nowrap" style={{ fontSize: '0.85rem' }}>
                        {isSingle && selected.length === 1 ? selected[0] : label}
                    </span>
                    {!isSingle && selected.length > 0 && (
                        <span className="text-dark fw-normal ms-1" style={{ fontSize: '0.75rem' }}>
                            ({selected.length})
                        </span>
                    )}
                </div>
                <IoChevronDownOutline size={12} className={`text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className={`dropdown-menu show p-2 shadow-lg border mt-1 responsive-dropdown-menu ${alignRight ? 'end-0' : 'start-0'}`} 
                     style={{ 
                         maxHeight: '350px', 
                         zIndex: 1060, 
                         borderRadius: '10px',
                         border: '1px solid #E1E7EF',
                         display: 'flex',
                         flexDirection: 'column'
                     }}>
                    
                    {/* Search Bar */}
                    <div className="position-relative mb-2">
                        <IoSearchOutline className="position-absolute translate-middle-y" 
                                        style={{ left: '10px', top: '50%', color: '#94A3B8' }} size={16} />
                        <input 
                            type="text" 
                            className="form-control form-control-sm ps-4 bg-light border-0 py-1" 
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ borderRadius: '6px', fontSize: '0.9rem', height: '36px' }}
                            autoFocus
                            aria-label={placeholder}
                        />
                    </div>
                    
                    {/* Options List */}
                    <div className="options-list overflow-auto px-1 flex-grow-1" style={{ maxHeight: '220px' }}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, idx) => {
                                const optName = opt.name || opt;
                                const isChecked = selected.includes(optName);
                                return (
                                    <div key={idx} 
                                         className="form-check p-1 rounded hover-bg-light transition-all cursor-pointer d-flex align-items-center gap-2 mb-0"
                                         onClick={() => handleToggle(optName)}
                                         style={{ minHeight: '34px' }}
                                         role="option"
                                         aria-selected={isChecked}
                                    >
                                        <input 
                                            className={`form-check-input ms-0 mt-0 cursor-pointer ${isSingle ? 'rounded-circle' : ''}`} 
                                            type={isSingle ? "radio" : "checkbox"} 
                                            checked={isChecked}
                                            onChange={() => {}} 
                                            style={{ width: '16px', height: '16px' }}
                                            aria-label={optName}
                                        />
                                        <label className="form-check-label w-100 cursor-pointer mb-0 text-dark fw-medium" 
                                               style={{ fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {optName}
                                        </label>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-3 text-muted">No results found</div>
                        )}
                    </div>

                    {/* Add Option Actions */}
                    {onAddOption && addOptionLabel && (
                        <div className="mt-2 pt-2 border-top">
                            <button
                                className="btn btn-sm btn-outline-primary w-100 fw-bold d-flex align-items-center justify-content-center gap-1 py-1"
                                style={{ borderRadius: '6px', fontSize: '0.85rem' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const newOpt = window.prompt(`Enter new ${addOptionLabel}:`);
                                    if (newOpt && newOpt.trim()) {
                                        const trimmed = newOpt.trim();
                                        const exists = options.find(o => (o.name || o).toLowerCase() === trimmed.toLowerCase());
                                        if (exists) {
                                            window.alert(`${addOptionLabel} "${trimmed}" already exists in the list.`);
                                        } else {
                                            onAddOption(trimmed);
                                        }
                                    }
                                }}
                            >
                                <IoAddOutline size={16} /> Add {addOptionLabel}
                            </button>
                        </div>
                    )}

                    {/* Footer Actions */}
                        <div className="d-flex gap-2 mt-2 pt-2 border-top">
                            <button className="btn btn-sm btn-outline-danger flex-grow-1 fw-bold py-1 responsive-action-btn" 
                                    onClick={(e) => { e.stopPropagation(); onClearAll(); }}
                                    style={{ borderRadius: '6px' }}
                                    aria-label={`Clear all ${label}`}
                            >
                                Clear
                            </button>
                            <button className="btn btn-sm btn-primary flex-grow-1 fw-bold py-1 responsive-action-btn" 
                                    onClick={() => setIsOpen(false)}
                                    style={{ background: 'linear-gradient(0deg, #012970, #024ed6)', border: 'none', borderRadius: '6px' }}
                                    aria-label={`Apply ${label} selection`}
                            >
                                Apply
                            </button>
                        </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;
